import React, { useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { push } from 'connected-react-router';

import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import { Typography, Card, Tooltip } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import WorkIcon from '@material-ui/icons/Work';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';

import { Stack } from 'components/layout';
import { setSearchState, setChannelWorkflowGridState, setPostAcqWorkflowGridState } from 'store/actions';
import { Constants, getModuleByLocation } from 'utilities/constants';
import { OpportunityStatusChip } from 'components/layout';
import TaskStatusChip from 'components/underwrite/postAcquisition/TaskStatusChip';

export const highlightSearchString = (text, searchString, classes) => {
  let oneSpaceSearchString = searchString.replace(/  +/g, ' ');//remove excess spacing
  if (text?.length > 0 && oneSpaceSearchString?.length > 0 && text.toUpperCase().indexOf(oneSpaceSearchString.toUpperCase()) !== -1) {
    let words = text.toUpperCase().split(oneSpaceSearchString.toUpperCase());
    let newArray = [];
    let newLocation = 0;
    for (let i = 0; i < words.length; i++) {
      let currentString = text.substring(newLocation, newLocation + words[i].length);
      let currentSeparator = ""
      if (newLocation + words[i].length + oneSpaceSearchString.length <= text.length) {
        currentSeparator = text.substring(newLocation + words[i].length, newLocation + words[i].length + oneSpaceSearchString.length);//preserve casing of separator
      }
      newLocation = newLocation + oneSpaceSearchString.length + words[i].length;
      newArray.push({ normalWord: currentString });
      newArray.push({ boldedWord: currentSeparator });
    }
    return (
      newArray.map((word, i) => {
        if (word.normalWord) {
          return (
            <Typography className={classes.inlineText} key={i}>
              {word.normalWord}
            </Typography>
          )
        } else {
          return (
            <Typography className={classes.inlineSearchText} key={i}>
              {word.boldedWord}
            </Typography>
          )
        }
      })
    )
  } else {
    return (
      <Typography className={classes.inlineText}>
        {text}
      </Typography>
    )
  }
}

// searchString may be a single id or a comma-separated list of ids (optionally spaced), so an id
// counts as "searched" when it matches any of the tokens, not just an exact match of the whole string.
const isSearchedId = (value, searchString) =>
  value != null && (searchString ?? '').replace(/\s+/g, '').split(',').includes(value.toString());

const highlightedId = (value, searchString, classes) => (
  <span className={isSearchedId(value, searchString) ? classes.inlineSearchText : classes.inlineText}>
    {value}
  </span>
);

const displayId = (resultType, id, assetId, searchString, classes) => {
  // Acq property cards show a single bare (highlightable) id, unchanged.
  if (resultType === 'property' && id) {
    return (
      <Typography className={isSearchedId(id, searchString) ? classes.inlineSearchText : classes.inlineText}>
        {id}
      </Typography>
    )
  }
  // Post-acq tasks are searchable by Task ID or Asset ID. Show only the one the search matched by,
  // preferring Task ID when the search hit both (same task) or matched the address instead — so the
  // card is always one id-line tall.
  if (resultType === 'task' && id) {
    const matchedByAssetId = assetId != null && !isSearchedId(id, searchString) && isSearchedId(assetId, searchString);
    return matchedByAssetId
      ? <Typography component="div">Asset ID: {highlightedId(assetId, searchString, classes)}</Typography>
      : <Typography component="div">Task ID: {highlightedId(id, searchString, classes)}</Typography>;
  }
  return null;
}

const getStatusInformation = (resultType, opportunityStatus, underwriteStatus, userId, taskStatus, consumedOn, postAcquisitionLoadCompletedOn) => {
  if (resultType === 'property' && opportunityStatus) {
    let txnOpp = {
      opportunityStatus: opportunityStatus,
      underwriteStatus: underwriteStatus,
      userId: userId
    }
    return (
      <Typography component="div">
        <OpportunityStatusChip txnOpp={txnOpp} />
      </Typography>
    )
  }
  if (resultType === 'task' && taskStatus) {
    let task = {
      taskStatus: taskStatus,
      underwriteStatus: underwriteStatus,
      consumedOn: consumedOn,
      assignedUser: userId,
      postAcquisitionLoadCompletedOn: postAcquisitionLoadCompletedOn
    }
    return (
      <Typography component="div">
        <TaskStatusChip task={task} />
      </Typography>
    )
  }
  return null;
}
const getIcon = resultType => {
  switch (resultType) {
    case 'property':
    case 'task':
      return <HomeIcon/>;
    case 'auction':
      return <WorkIcon/>;
    case 'import':
      return <CreateNewFolderIcon/>;
    default:
      return null;
  }
}

const getAuditInformation = (resultType, importedBy, importedOn, latestPricingDateTime) => {
  switch (resultType) {
    case 'property':
    case 'task':
      // if there is a latestPricingDateTime, the search result was from the user clicking My Last 100 Priced button
      if (latestPricingDateTime) {
        return (
          <Typography>
            {`Last valuated on ${latestPricingDateTime?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
          </Typography>
        )
      } else {
        // Acq: importedOn is the TransactionOpportunity.CreatedOn — the original creation date, which differs
        // from Import.CreatedOn/CompletedOn (the latest imported-on), hence "Initially imported on".
        // Post-acq tasks have no such re-import distinction, so they read "Imported on".
        const importedLabel = resultType === 'task' ? 'Imported on' : 'Initially imported on';
        return (
          <Typography>
            {`${importedLabel} ${importedOn?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}${importedBy ? ` by ${importedBy}` : ''}`}
          </Typography>
        )
      }
    case 'auction':
      return (
        <Typography>
          {`Imported on ${importedOn?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}${importedBy ? ` by ${importedBy}` : ''}`}
        </Typography>
      )
    case 'import':
      return (
        <Typography>
          {`Imported on ${importedOn?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}${importedBy ? ` by ${importedBy}` : ''}`}
        </Typography>
      )
    default:
      return null;
  }
}

const getPortfolioInformation = (resultType, portfolioName, classes) => {
  if (resultType === 'property' && portfolioName) {
    return (
      <Typography component="div">
        {`${portfolioName}`}<WorkIcon className={classes.inlineIcon}/>
      </Typography>
    )
  }
  return null;
}

const SearchCard = ({ classes, resultType, title, data, importedBy, importedOn, portfolioName, id, assetId, channel, searchString, push,
  setSearchState, setWorkflowGridState, opportunityStatus, underwriteStatus, userId, latestPricingDateTime, taskStatus, consumedOn, postAcquisitionLoadCompletedOn, requestType, module }) => {
  // Post-acq results (task/import) carry a requestType and show it here; acq results fall back to channel.
  const secondaryLabel = requestType || channel;
  // A task can only be opened once it's been loaded; unloaded tasks show as a disabled, non-clickable
  // card so the user knows to load it from the workflow grid first (mirrors the grid's own open gate).
  const isUnloadedTask = resultType === 'task' && !postAcquisitionLoadCompletedOn;
  const titleDisplay = useMemo(() => {
    return highlightSearchString(title, searchString, classes);
  }, [title, searchString]);

  const dataDisplay = useMemo(() => {
    return data.map((str, i) => {
      return (
        <Typography key={i}>
          {str}
        </Typography>
      );
    });
  }, [data]);

  const idDisplay = useMemo(() => {
    return displayId(resultType, id, assetId, searchString, classes);
  }, [id, assetId, searchString]);

  const iconDisplay = useMemo(() => {
    return getIcon(resultType);
  }, []);

  const auditDisplay = useMemo(() => {
    return getAuditInformation(resultType, importedBy, importedOn, latestPricingDateTime);
  }, []);

  const portfolioDisplay = useMemo(() => {
    return getPortfolioInformation(resultType, portfolioName, classes);
  }, []);
  const statusChipDisplay = useMemo(() => {
    return getStatusInformation(resultType, opportunityStatus, underwriteStatus, userId, taskStatus, consumedOn, postAcquisitionLoadCompletedOn);
  }, []);
  const onClick = e => {
    if (isUnloadedTask) return; // not openable until loaded from the workflow grid
    switch (resultType) {
      case 'property':
        push(`${Constants.Routes.UNDERWRITE_PROPERTY}/${id}`)
        break;
      case 'task':
        push(`${Constants.Routes.POSTACQ_TASK}/${id}`)
        break;
      case 'auction':
        push(`${Constants.Routes.UNDERWRITE_AUCTION}/${id}`)
        break;
      case 'import':
        setWorkflowGridState({ filterValue: [['importId', '=', id]], columnMods: [{ field: 'id', type: 'sortOrder', value: 'asc' }] }, true);
        push(module === Constants.Module.POSTACQ ? Constants.Routes.POSTACQ_WORKFLOW : Constants.Routes.CHANNEL_WORKFLOW);
        break;
      default:
        break;
    }
    setSearchState({ searchIsOpen: false });
  }

  const card = (
    <Card className={classNames(classes.searchCard, isUnloadedTask && classes.searchCardDisabled)} onClick={onClick}>
      <Stack horizontal>
        <div className={classes.cardSection}>
          {iconDisplay}
        </div>
        <div className={classNames(classes.cardMainContent, classes.cardSection)}>
          {titleDisplay}
          {dataDisplay}
          {auditDisplay}
          {portfolioDisplay}
        </div>
        <div className={classes.cardSection} style={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
          {idDisplay}
          {secondaryLabel &&
            <Typography>
              {secondaryLabel}
            </Typography>
          }
          {statusChipDisplay}
        </div>
      </Stack>
    </Card>
  )

  return isUnloadedTask
    ? <Tooltip title="Task must be loaded from the workflow grid to open">{card}</Tooltip>
    : card;
}

const mapStateToProps = (state, ownProps) => ({
  module: getModuleByLocation(ownProps.location)
});

const mapDispatchToProps = (dispatch, ownProps) => {
  const module = getModuleByLocation(ownProps.location);
  const workflowGridUpdateAction = module === Constants.Module.UNDERWRITE ? setChannelWorkflowGridState : setPostAcqWorkflowGridState;
  return {
    push: route => dispatch(push(route)),
    setWorkflowGridState: (state, isUpdate) => dispatch(workflowGridUpdateAction(state, isUpdate)),
    setSearchState: state => dispatch(setSearchState(module, state)),
  }
};

const styles = theme => ({
  searchCard: {
    margin: '10px 20px',
    cursor: 'pointer',
    "&:hover": {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    }
  },
  searchCardDisabled: {
    cursor: 'default',
    opacity: 0.55,
    "&:hover": {
      backgroundColor: 'transparent'
    }
  },
  cardMainContent: {
    flexGrow: 1,
    padding: 10
  },
  cardSection: {
    padding: 10
  },
  inlineText: {
    display: 'inline-block',
    whiteSpace: 'pre'
  },
  inlineSearchText: {
    display: 'inline-block',
    whiteSpace: 'pre',
    fontWeight: 'bold',
    backgroundColor: 'yellow'
  },
  inlineIcon: {
    verticalAlign: 'bottom',
    margin: '0 3px'
  }
});

export default compose(
  withRouter,
  withStyles(styles),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(SearchCard);
